import React from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { useLanguage } from '@/context/LanguageContext';
import { cn, formatDurationLong } from '@/utils';
import {
  Phone,
  CheckCircle,
  Clock,
  Timer,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Zap,
} from 'lucide-react';

interface MetricCardsProps {
  metrics: {
    totalCalls: number;
    resolvedCalls: number;
    pendingCalls: number;
    avgDuration: number;
    
    // New metrics
    highUrgencyRate?: number;
    atRiskCount?: number;
    topPeakHour?: { hour: number; count: number };
  };
  loading: boolean;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: { value: number; positive: boolean };
  color: 'blue' | 'green' | 'amber' | 'purple' | 'red';
}

function MetricCard({ title, value, subtitle, icon, trend, color }: MetricCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400',
    green: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400',
    purple: 'bg-purple-50 text-purple-600 dark:bg-purple-950 dark:text-purple-400',
    red: 'bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400',
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold tracking-tight">{value}</p>
            {subtitle && (
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            )}
            {trend && (
              <div className={cn(
                'flex items-center gap-1 text-sm font-medium',
                trend.positive ? 'text-emerald-600' : 'text-red-600'
              )}>
                {trend.positive ? (
                  <TrendingUp className="h-4 w-4" />
                ) : (
                  <TrendingDown className="h-4 w-4" />
                )}
                {Math.abs(trend.value)}%
              </div>
            )}
          </div>
          <div className={cn('p-3 rounded-xl', colorClasses[color])}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function MetricCards({ metrics, loading }: MetricCardsProps) {
  const { t, language } = useLanguage();

  const resolvedPercentage = metrics.totalCalls > 0
    ? Math.round((metrics.resolvedCalls / metrics.totalCalls) * 100)
    : 0;
  const pendingPercentage = metrics.totalCalls > 0
    ? Math.round((metrics.pendingCalls / metrics.totalCalls) * 100)
    : 0;

  // Helper to format hour
  const formatHour = (hour: number) => {
    if (language === 'en') {
      if (hour === 0) return '12 AM';
      if (hour < 12) return `${hour} AM`;
      if (hour === 12) return '12 PM';
      return `${hour - 12} PM`;
    }
    return `${hour}:00`;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="space-y-3">
                <div className="h-4 bg-muted rounded w-1/2" />
                <div className="h-8 bg-muted rounded w-3/4" />
                <div className="h-3 bg-muted rounded w-1/3" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <MetricCard
        title={t('metrics.totalCalls')}
        value={metrics.totalCalls.toLocaleString()}
        subtitle={`${t('metrics.resolutionRate')}: ${resolvedPercentage}%`}
        icon={<Phone className="h-6 w-6" />}
        color="blue"
      />
      <MetricCard
        title={t('metrics.resolved')}
        value={metrics.resolvedCalls.toLocaleString()}
        subtitle={`${resolvedPercentage}%`}
        icon={<CheckCircle className="h-6 w-6" />}
        color="green"
      />
      <MetricCard
        title={t('metrics.pending')}
        value={metrics.pendingCalls.toLocaleString()}
        subtitle={`${pendingPercentage}%`}
        icon={<Clock className="h-6 w-6" />}
        color="amber"
      />
      <MetricCard
        title={t('metrics.avgDuration')}
        value={formatDurationLong(metrics.avgDuration)}
        subtitle={t('metrics.avgCallLength')}
        icon={<Timer className="h-6 w-6" />}
        color="purple"
      />
      
      {/* NEW: High Urgency Rate Card */}
      <MetricCard
        title={t('metrics.highUrgencyRate')}
        value={`${metrics.highUrgencyRate || 0}%`}
        subtitle={t('metrics.highExtraHighUrgency')}
        icon={<AlertTriangle className="h-6 w-6" />}
        color="red"
      />
      
      {/* NEW: At-Risk Calls Card */}
      <MetricCard
        title={t('metrics.atRiskCalls')}
        value={(metrics.atRiskCount || 0).toLocaleString()}
        subtitle={t('metrics.highUrgencyPending')}
        icon={<Clock className="h-6 w-6" />}
        color="amber"
      />
      
      {/* NEW: Peak Hour Card */}
      {metrics.topPeakHour && (
        <MetricCard
          title={t('metrics.peakHour')}
          value={formatHour(metrics.topPeakHour.hour)}
          subtitle={`${metrics.topPeakHour.count} ${t('metrics.calls')}`}
          icon={<Zap className="h-6 w-6" />}
          color="blue"
        />
      )}
      
      {/* NEW: Resolution Rate Card */}
      <MetricCard
        title={t('metrics.resolutionRateCard')}
        value={`${resolvedPercentage}%`}
        subtitle={t('metrics.ofAllCalls')}
        icon={<CheckCircle className="h-6 w-6" />}
        color="green"
      />
    </div>
  );
}
